import { Button, Modal } from 'flowbite-react'
import React, { useEffect, useState } from 'react'
import OTPInput from 'react-otp-input'
import "../OtpModal/OtpModal.css"
import { mobileOTPSignup, mobileOTPSignUpVerify } from '../../api/auth'
import toast from 'react-hot-toast'
import FormModal from '../FormModal/FormModal'
import { handleVerifyResponse, showExistingAccountHint } from '../../utils/landingAuth'
import AccountHintBanner from '../AccountHintBanner/AccountHintBanner'
import FieldError, { ButtonSpinner } from '../FieldError/FieldError'
import { getApiErrorMessage } from '../../utils/landingForm'
import { useNavigate } from 'react-router-dom'

const OtpVerifyVisitor = ({
    isOpenverifyModal,
    toggleModal,
    mobile,
    verifyOtp,
    toggleFormChitAd,
    accountHint,
    onAuthComplete,
}) => {
    const navigate = useNavigate();
    const [otp, setOtp] = useState('');
    const [openForm, setOpenForm] = useState(false);
    const [hint, setHint] = useState(accountHint || null);
    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);

    useEffect(() => {
        setHint(accountHint || null);
        setError('');
        setOtp('');
    }, [accountHint, isOpenverifyModal, verifyOtp]);

    const resetOtpUi = () => {
        setOtp('');
        setError('');
        setHint(null);
        setVerifying(false);
        setResending(false);
    };

    const closeOtpModal = () => {
        resetOtpUi();
        if (isOpenverifyModal) toggleModal?.();
        if (verifyOtp) toggleFormChitAd?.();
    };

    const toggleForm = () => {
        setOpenForm(!openForm)
    }

    const otpTrigger = async () => {
        if (!mobile || String(mobile).length !== 10 || resending || verifying) return;
        setResending(true);
        setError('');
        try {
            const mobileStatus = await mobileOTPSignup({
                mobile,
                visitor: true,
            });
            if (mobileStatus?.status >= 400) {
                setError(getApiErrorMessage(mobileStatus, 'Could not resend OTP'));
                return;
            }
            const nextHint = showExistingAccountHint(mobileStatus);
            if (nextHint) setHint(nextHint);
            toast.success('OTP Resend Successfully!');
        } catch (err) {
            setError(getApiErrorMessage(err, 'Could not resend OTP. Please try again.'));
        } finally {
            setResending(false);
        }
    }

    const otpVerify = async () => {
        if (!otp || otp.length !== 6) {
            setError('Please enter the 6-digit OTP.');
            return;
        }
        setVerifying(true);
        setError('');
        try {
            const payload = {
                mobile,
                otp,
                visitor: true,
                device_id: 'landing-web',
                ip: 'landing-page',
            }
            const updateApi = await mobileOTPSignUpVerify(payload)
            const outcome = handleVerifyResponse(updateApi, mobile, {
                onCloseModals: ({ clearInputs = true, action } = {}) => {
                    closeOtpModal();
                    onAuthComplete?.({ clearInputs, action });
                },
                onShowRegistrationForm: () => setOpenForm(true),
                onGuestReady: () => {
                    navigate('/join');
                },
            })

            if (!outcome.ok) {
                setError(outcome.message || 'OTP verification failed')
                return
            }

            toast.success(outcome.message || 'OTP Verified Successfully')
        } catch (err) {
            setError(getApiErrorMessage(err, 'OTP verification failed. Please try again.'));
        } finally {
            setVerifying(false);
        }
    }

    const busy = verifying || resending;

    return (
        <>
            <Modal className='z-[2000]' show={isOpenverifyModal || verifyOtp} style={{ width: 'auto' }} onClose={() => {
                closeOtpModal();
            }} popup>
                <Modal.Body>
                    <div className="text-center p-6">
                        <h1 className='text-2xl mb-3 font-bold'>OTP Verification</h1>
                        <h3 className="mb-2 text-md font-normal text-gray-500 dark:text-gray-400">
                            Enter the OTP you received at <span className='text-navydark font-bold'>+91 {mobile}</span>
                        </h3>
                        <AccountHintBanner message={hint} className="mb-4 text-left" />
                        <div className='flex flex-col items-center'>
                            <OTPInput
                                value={otp}
                                onChange={(value) => {
                                    setOtp(value);
                                    if (error) setError('');
                                }}
                                inputType="tel"
                                numInputs={6}
                                inputStyle={`custom-otp-field${error ? ' otp-field-error' : ''}`}
                                renderInput={(props) => (
                                    <input {...props} disabled={busy} aria-invalid={Boolean(error)} />
                                )}
                            />
                            <FieldError message={error} className="w-full max-w-[280px] text-center mt-2" />
                        </div>
                        <div className="flex justify-center mt-5">
                            <Button
                                color="dark"
                                size='sm'
                                className='bg-primaryColor'
                                disabled={busy || !otp || otp.length !== 6}
                                onClick={otpVerify}
                            >
                                {verifying ? (
                                    <span className="inline-flex items-center gap-2">
                                        <ButtonSpinner /> Verifying…
                                    </span>
                                ) : (
                                    'Verify'
                                )}
                            </Button>
                        </div>
                        <p className='text-sm mt-3 text-gray-500'>
                            Didn't receive code?{' '}
                            <span
                                className={`underline ${busy ? 'cursor-not-allowed text-gray-400' : 'text-teal-600 cursor-pointer'}`}
                                onClick={busy ? undefined : otpTrigger}
                            >
                                {resending ? 'Sending…' : 'Request again'}
                            </span>
                        </p>
                    </div>
                </Modal.Body>
            </Modal>

            <FormModal
                isOpenForm={openForm}
                toggleForm={toggleForm}
                mobile={mobile}
                onHandoffComplete={() => {
                    setOpenForm(false);
                    resetOtpUi();
                    onAuthComplete?.({ clearInputs: true, action: 'handoff_after_registration' });
                }}
            />
        </>
    )
}

export default OtpVerifyVisitor
